import { http } from './http'
import { RegionSchema, TokenSchema } from '../schemas'
import { z } from 'zod'
import { getLoginUrl, serverVariables } from '../lib'
import { ls } from '../local-storage'
import { parseWithZod } from '../utils/parse-with-zod'

// Clear session on Unauthorized (401) responses
http.onUnauthorized(() => auth.clearSession())
// Let the http client recover from an expired access token by refreshing it and retrying. Forced:
// the server has just rejected the access token, so the stored token's expiry can't be trusted.
http.setSessionRefresher(() => auth.forceRefreshToken())

let userRegionHost = ''
let userRegions: z.infer<typeof RegionSchema>[] = []

function hasWebLocks() {
  return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function'
}

/**
 * Always fetch the token from the server the first time the app loads, so a tab never starts
 * from a token another tab left in local storage that the server may no longer honour.
 */
let isGettingTokenFirstTime = true

/**
 * A token whose access half expires within this margin is treated as stale and refreshed, so a
 * request never goes out on an access token that expires in flight.
 */
const ACCESS_TOKEN_EXPIRY_MARGIN_S = 60

function hasFreshAccessToken(token: z.infer<typeof TokenSchema> | null | undefined) {
  return !!token && token.exp_access - Date.now() / 1000 > ACCESS_TOKEN_EXPIRY_MARGIN_S
}

async function requestToken({ otp, force = false }: { otp?: string; force?: boolean }) {
  // Use web locks to prevent concurrent refresh token requests across tabs
  const refresh = async () => {
    if (import.meta?.env?.DEV && !otp && !force) {
      const currentUserToken = ls.getItem('clofoToken')
      if (currentUserToken) {
        setUserRegionHost(currentUserToken.subscription_region_id, currentUserToken.regions)
        return currentUserToken
      }
    }

    // Another tab may have refreshed while this one waited for the lock (or was frozen)
    if (!otp && !force && !isGettingTokenFirstTime) {
      const currentUserToken = ls.getItem('clofoToken')
      if (currentUserToken && hasFreshAccessToken(currentUserToken)) {
        setUserRegionHost(currentUserToken.subscription_region_id, currentUserToken.regions)
        return currentUserToken
      }
    }
    isGettingTokenFirstTime = false
    const result = await http.post('/api/v1/auth/tokens/refresh', otp ? { otp } : undefined)
    const token = parseWithZod(TokenSchema, result)
    ls.setItem('clofoToken', token)
    setUserRegionHost(token.subscription_region_id, token.regions)
    await setTokenInIndexedDB(token)
    return token
  }

  if (!hasWebLocks()) {
    return await refresh()
  }

  return await navigator.locks.request('clofo/refresh-token', refresh)
}

export const auth = {
  getUserRegionHost() {
    // Prefer server variables over token-based region host
    const serverRegionHost = serverVariables.regionHost
    if (serverRegionHost) return `${location.protocol}//${serverRegionHost}`

    // For subscribers, userRegionHost is set from subscription_region_id
    if (userRegionHost) return `${location.protocol}//${userRegionHost}`

    // For non-subscribers (external/guest users), subscription_region_id is null so
    // userRegionHost is empty. Fall back to matching the deal's region from the URL
    // path against the token's regions list.
    const dealRegionId = serverVariables.regionId
    if (dealRegionId) {
      const region = userRegions.find(r => r.id === dealRegionId)
      if (region) return `${location.protocol}//${region.host}`
    }

    if (import.meta?.env?.DEV) {
      return location.origin
    }

    return `${location.protocol}//`
  },

  async getPusherConfig() {
    const regionHost = this.getUserRegionHost()
    const config = await http.get(`${regionHost}/api/sys/config`)
    return parseWithZod(PusherConfigSchema, config)
  },

  /**
   * Refreshes the auth token. Every tab runs this on its own timer; the web lock serialises them
   * and the freshness check means only the first tab through actually hits the server.
   * There is deliberately no long-lived "leader" tab: a leader that Chrome freezes stops
   * refreshing for every tab and the session expires underneath them.
   */
  refreshToken(otp?: string) {
    return requestToken({ otp })
  },

  /**
   * Refreshes the auth token with the server, never reusing the stored one. Use it when the
   * server has rejected the access token: the stored token can still look fresh then (the client
   * clock lags the server's, or the token was revoked before its expiry), and reusing it would
   * leave the rejected cookies in place.
   */
  forceRefreshToken() {
    return requestToken({ force: true })
  },

  /**
   * Refreshes straight away if the stored access token has expired or is about to. Called when a
   * tab wakes up: timers don't run while Chrome has a tab frozen, so the scheduled refresh may
   * have been missed.
   */
  refreshIfStale() {
    const token = ls.getItem('clofoToken')
    if (token && !hasFreshAccessToken(token)) {
      void http.refreshSession()
    }
  },

  async loginMethod({ email }: { email: string }) {
    const result = await http.post('/api/v1/auth/login_method', { email })
    return parseWithZod(LoginMethodResponseSchema, result)
  },

  // Not oauth/exchange_dms_code: that one is the authenticated in-app connect flow.
  async exchangeDmsLoginCode({
    email,
    code,
    stateToken,
  }: {
    email: string
    code: string
    stateToken: string
  }) {
    const result = await http.post('/api/v1/auth/exchange_dms_login_code', {
      email,
      code,
      state_token: stateToken,
    })
    return parseWithZod(TokenSchema, result)
  },

  async loginWithPassword({
    email,
    password,
    remember,
  }: {
    email: string
    password: string
    remember: boolean
  }) {
    const result = await http.post('/api/v1/auth/tokens/obtain', {
      email,
      password,
      remember,
    })
    return parseWithZod(TokenSchema, result)
  },

  async resetPassword(email: string) {
    await http.post('/api/v1/auth/forgot_password/request', {
      email,
    })
  },

  // Validates a reset link's uid/token pair before showing the new-password form. The backend
  // returns 200 with an empty body when valid and 400 when the token is expired or already used.
  async checkResetToken(payload: { uidb64: string; token: string }) {
    await http.post('/api/v1/auth/forgot_password/check', payload)
  },

  async completeResetPassword(payload: {
    uidb64: string
    token: string
    new_password1: string
    new_password2: string
  }) {
    await http.post('/api/v1/auth/forgot_password/complete', payload)
  },

  async disableTfa(userId: number, payload: { otp: string }) {
    await http.post(`/api/v1/users/${userId}/disable_tfa`, payload)
  },

  async generateTfaSecret(userId: number) {
    const result = await http.post(`/api/v1/users/${userId}/generate_tfa_secret`)
    return parseWithZod(TfaSecretSchema, result)
  },

  async changePassword(payload: {
    email: string
    current_password: string
    new_password1: string
    new_password2: string
  }) {
    return await http.post('/api/v1/auth/change_password', payload)
  },

  async obtainNewToken({
    email,
    password,
    remember,
  }: {
    email: string
    password: string
    remember: boolean
  }) {
    const result = await http.post('/api/v1/auth/tokens/obtain', {
      email,
      password,
      remember,
    })
    return parseWithZod(TokenSchema, result)
  },

  async logout() {
    await http.post('/api/v1/auth/tokens/logout')
    await this.clearSession()
    window.location.href = getLoginUrl()
  },

  async clearSession() {
    const currentAuth = ls.getItem('clofoToken')
    if (currentAuth?.tfa_status === 'sso_verified') {
      await deleteIndexedDBKey('cachedEmail')
    }
    setUserRegionHost(null, [])
    ls.removeItem('clofoToken')
    await deleteIndexedDBKey('token')
    logoutChannel.postMessage({ type: 'logout' })
  },
}

const logoutChannel = new BroadcastChannel('clofo-logout')
logoutChannel.addEventListener('message', event => {
  // A tab already on the login app (`/apps/login`, or `/react/<pr-tag>/login/` in previews) stays
  // put: redirecting would carry the login page itself as `next`.
  if (event.data.type === 'logout' && !/\/login(\/|$)/.test(window.location.pathname)) {
    http.redirectToLoginWithNextUrl()
  }
})

const LoginMethodResponseSchema = z.object({
  method: z.enum(['password', 'sso', 'dms']),
  url: z.string(),
})

const PusherConfigSchema = z.object({
  pusher_app_cluster: z.string(),
  pusher_app_key: z.string(),
})

export type PusherConfig = z.infer<typeof PusherConfigSchema>

const TfaSecretSchema = z.object({
  tfa_secret_uri: z.string(),
})

function setUserRegionHost(
  subscriptionRegionId: number | null,
  regions: z.infer<typeof RegionSchema>[],
) {
  userRegions = regions || []
  userRegionHost = regions?.find(region => region.id === subscriptionRegionId)?.host || ''
}

/**
 * IndexedDB utility functions for storing and deleting auth-related data.
 * This is used to persist the auth token and cached email across sessions and tabs.
 * The token is stored in camelCase format to be consistent with the existing Ember app.
 */
const DATABASE_NAME = 'AuthDatabase'
const TABLE_NAME = 'auth'

function getIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(TABLE_NAME)) {
        db.createObjectStore(TABLE_NAME, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

const setTokenInIndexedDB = async (token: z.infer<typeof TokenSchema>): Promise<void> => {
  try {
    const authdb = await getIndexedDB()
    const transaction = authdb.transaction([TABLE_NAME], 'readwrite')
    const store = transaction.objectStore(TABLE_NAME)

    // Convert token keys to camelCase for consistency with the ember app
    const tokenCamelCase = tokenToCamelCase(token)
    store.put({
      key: 'token',
      value: tokenCamelCase,
    })
    authdb.close()
  } catch (error) {
    console.warn('Failed to store token in IndexedDB:', error)
  }
}

const deleteIndexedDBKey = async (key: 'cachedEmail' | 'token'): Promise<void> => {
  try {
    const authdb = await getIndexedDB()
    const transaction = authdb.transaction([TABLE_NAME], 'readwrite')
    const store = transaction.objectStore(TABLE_NAME)

    store.delete(key)
    authdb.close()
  } catch (error) {
    // Silently handle IndexedDB errors - the cached email clearing is not critical
    console.warn(`Failed to clear key ${key} from IndexedDB:`, error)
  }
}

function tokenToCamelCase(token: z.infer<typeof TokenSchema>) {
  return {
    email: token.email,
    expAccess: token.exp_access,
    expRefresh: token.exp_refresh,
    iatAccess: token.iat_access,
    iatRefresh: token.iat_refresh,
    organizationId: token.organization_id,
    regions: token.regions,
    subscriptionRegionId: token.subscription_region_id,
    tfaStatus: token.tfa_status,
    userId: token.user_id,
  }
}
